import { CorrectionFeedback } from '@entities/lesson'
import { useTranslation } from '@shared/config/i18n'
import { clsx } from 'clsx'
import React from 'react'

import { Box, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  feedback: CorrectionFeedback
}

/**
 * KR(일본 사용자 대상 한국어) 수업의 교정 피드백.
 *
 * 영어/일본어 카드를 재사용할 수 없는 이유는 문장 조각을 잇는 방식이 다르기 때문이다.
 * - 영어: `student_sentence` 가 단어 단위로 쪼개져 있어 조각 사이에 공백을 넣어 준다.
 * - 한국어/일본어: 글자 단위로 쪼개져 있고 공백도 하나의 원소로 들어 있다.
 *   여기에 영어처럼 공백을 넣으면 "안 녕 하 세 요" 로 벌어진다.
 *
 * 일본어 카드와도 다르다 — 그쪽은 후리가나(PronounceRubyRequest)를 붙이는데
 * 한국어에는 루비가 없고 `*_diction_*` 필드도 내려오지 않는다.
 */
export const KoreanCorrectionFeedback = ({ feedback }: Props) => {
  const t = useTranslation()

  // 공백을 덧붙이지 않는다. 배열 원소에 공백이 이미 들어 있다.
  const studentAnswer = feedback.phrase.student_sentence.map((studentSentencePart: string, index: number) => (
    <span
      key={`student-sentence-${index}`}
      className={clsx(feedback.phrase.student_highlight.includes(index) && 'text-red-normal')}
    >
      {studentSentencePart}
    </span>
  ))
  const masterAnswer = feedback.phrase.ai_sentence.map((masterSentencePart: string, index: number) => (
    <span
      key={`ai-sentence-${index}`}
      className={clsx(feedback.phrase.ai_highlight.includes(index) && 'text-blue-normal')}
    >
      {masterSentencePart}
    </span>
  ))

  return (
    <React.Fragment>
      <VStack className="gap-2">
        <HStack>
          <Box className="border rounded-[14px] px-3 py-[3px] bg-white border-gray-200">
            <Typography size="body1" className="font-medium">
              {t('lesson.feedbackLabels.myExpression')}
            </Typography>
          </Box>
        </HStack>
        <VStack className="gap-0.5">
          <Typography size="h3" className="font-medium" lang="ko">
            {studentAnswer}
          </Typography>
        </VStack>
      </VStack>

      <Box className="border divide-dashed" />

      <VStack className="gap-2">
        <HStack>
          <Box className="border rounded-[14px] px-3 py-[3px] bg-white border-gray-200">
            <Typography size="body1" className="font-medium">
              {t('lesson.feedbackLabels.correctedExpression')}
            </Typography>
          </Box>
        </HStack>
        <VStack className="gap-0.5">
          <Typography size="h3" className="font-medium" lang="ko">
            {masterAnswer}
          </Typography>
        </VStack>
      </VStack>

      <Box className="border divide-dashed" />

      {/* 해설은 학습자 모국어인 일본어로 내려온다. 공백이 없어 줄바꿈이 안 되므로 가드를 둔다. */}
      <VStack className="gap-2 min-w-0 max-w-full overflow-hidden">
        <HStack>
          <Box className="border rounded-[14px] px-3 py-[3px] bg-white border-gray-200">
            <Typography size="body1" className="font-medium">
              {t('lesson.feedbackLabels.feedback')}
            </Typography>
          </Box>
        </HStack>
        <VStack className="gap-0.5 min-w-0 max-w-full overflow-hidden">
          <Typography size="body1" className="text-gray-500">
            {feedback.phrase.ai_explanation}
          </Typography>
        </VStack>
      </VStack>
    </React.Fragment>
  )
}
